import React, { useState } from "react";
import {
  FaGithub, FaChevronDown, FaFlask,
  FaBriefcase, FaRocket, FaGlobe,
} from "react-icons/fa";
import MediaEmbed from "./shared/MediaEmbed";
import "./Projects.css";

// ─── Static project data ──────────────────────────────────────────────────
const PROJECTS = [
  {
    id: "water",
    title: "Global Water Consumption Analysis",
    type: "academic",
    description: "In-depth analysis of global water consumption patterns using Apache Spark for distributed processing. Covers agricultural, industrial, and household usage across countries with trend analysis and groundwater depletion insights.",
    tech: ["Python", "Apache Spark", "PySpark", "SQL", "Matplotlib", "Pandas"],
    challenges: [
      "Processing large-scale datasets efficiently using distributed computing",
      "Cleaning inconsistent country-level records and handling missing values at scale",
      "Writing optimised Spark SQL queries for cross-continental aggregations",
    ],
    gains: [
      "Hands-on Big Data processing with Apache Spark and PySpark",
      "Distributed SQL querying and DataFrame manipulation",
      "Data visualisation storytelling with Matplotlib",
    ],
    githubUrl: "https://github.com/ela2002/Global-Water-Consumption-Analysis",
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: null,
  },
  {
    id: "cancer",
    title: "Breast Cancer Diagnosis Prediction",
    type: "academic",
    description: "Comparison of 9 machine learning models (Logistic Regression, SVM, KNN, Random Forest, AdaBoost, Stacking, etc.) for predicting benign vs malignant tumors using the Wisconsin WDBC dataset. Includes SMOTE for class imbalance.",
    tech: ["Python", "Scikit-Learn", "SMOTE", "Pandas", "NumPy", "Seaborn", "Matplotlib"],
    challenges: [
      "Handling class imbalance between benign and malignant samples",
      "Selecting and tuning 9 distinct models for fair comparison",
      "Interpreting feature importance from Random Forest for medical context",
    ],
    gains: [
      "Ensemble methods: Bagging, Boosting, Stacking",
      "Imbalanced dataset handling with SMOTE oversampling",
      "ROC curves, confusion matrices, multi-model evaluation",
    ],
    githubUrl: "https://github.com/ela2002/Breast_Cancer_Detection",
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: "/projects/D%C3%A9tection%20du%20Cancer%20du%20Sein%20Utilisant%20le%20Machine%20Learning.pdf",
  },
  {
    id: "facial",
    title: "Facial Recognition — SIFT + HOG + PCA + SVM/KNN",
    type: "academic",
    description: "Robust facial recognition system fusing SIFT and HOG visual features, reducing dimensionality with PCA, then classifying with SVM and KNN. Evaluated on the FEI Face Database (2800 images, 200 individuals).",
    tech: ["Python", "OpenCV", "Scikit-Learn", "PCA", "SVM", "KNN", "Matplotlib", "NumPy"],
    challenges: [
      "Extracting and concatenating heterogeneous SIFT + HOG feature vectors",
      "Managing high dimensionality of fused feature space before PCA",
      "Evaluating under varied lighting, expressions and head poses",
    ],
    gains: [
      "Computer vision: SIFT keypoints, HOG descriptor computation",
      "Feature fusion and dimensionality reduction strategies",
      "Combining SVM and KNN for improved recognition accuracy",
    ],
    githubUrl: "https://github.com/ela2002/Facial-Recognition-Project",
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: "/projects/Facial%20Recognition.pdf",
  },
  {
    id: "retail",
    title: "Data Mining — Online Retail Analysis",
    type: "academic",
    description: "Comprehensive data mining analysis of ~540k UCI Online Retail transactions. Includes RFM customer segmentation, K-Means and hierarchical clustering (CAH), and market basket analysis with Apriori and FP-Growth.",
    tech: ["Python", "Pandas", "K-Means", "CAH", "Apriori", "FP-Growth", "Matplotlib", "Seaborn"],
    challenges: [
      "Cleaning and preprocessing 540,000+ transaction rows with missing values",
      "Choosing optimal K for clustering using elbow method and silhouette scores",
      "Tuning support/confidence thresholds for meaningful association rules",
    ],
    gains: [
      "RFM (Recency, Frequency, Monetary) customer segmentation",
      "Market basket analysis and association rule mining",
      "Hierarchical clustering interpretation and dendrogram analysis",
    ],
    githubUrl: "https://github.com/ela2002/Shop-Insight",
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: "/projects/Analyse%20et%20segmentation%20des%20comportements%20d%27achat.pdf",
  },
  {
    id: "housing",
    title: "Student Housing Management System",
    type: "academic",
    description: "Spring Boot REST API for managing student housing — universities, dormitories, blocks, rooms, students, and reservations. Full CRUD operations with RESTful endpoints testable via Postman.",
    tech: ["Java 17+", "Spring Boot 3.x", "Spring Data JPA", "MySQL", "Lombok", "Maven", "REST APIs"],
    challenges: [
      "Designing a normalised relational schema for complex entity relationships",
      "Implementing JPA associations (OneToMany, ManyToOne) correctly",
      "Building a clean RESTful API following HTTP conventions",
    ],
    gains: [
      "Enterprise Java with Spring Boot and JPA",
      "RESTful API design and Postman testing workflow",
      "Database schema design with MySQL",
    ],
    githubUrl: "https://github.com/ela2002/jee_project1",
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: null,
  },
  {
    id: "onboardio",
    title: "Onboardio — Employee Onboarding Web App",
    type: "academic",
    description: "Web application that streamlines the onboarding process for managers by automating welcome emails to new employees. React frontend, Node.js/Express backend, Firebase for authentication and email-sending via Nodemailer.",
    tech: ["React", "Node.js", "Express", "Firebase Auth", "Nodemailer", "JavaScript"],
    challenges: [
      "Configuring Firebase Authentication alongside custom backend email flows",
      "Designing customisable email templates with variable injection for each new hire",
      "Keeping the interface intuitive for non-technical managers",
    ],
    gains: [
      "Full-stack integration of Firebase Auth with a Node.js/Express API",
      "Email automation with Nodemailer and template management",
      "End-to-end UX design for a business process workflow",
    ],
    githubUrl: "https://github.com/ela2002/Onboardio",
    liveUrl: null,
    imgSrc: null,
    videoSrc: "/projects/onboardio.mp4",
    videoSrc2: null,
    pdfSrc: null,
  },
  {
    id: "pma",
    title: "Project Manager App",
    type: "academic",
    description: "Modern web app for managing projects and tasks. React (Vite) frontend, NestJS backend, Firebase for authentication and Firestore for real-time data. Full CRUD for projects and their associated tasks.",
    tech: ["React (Vite)", "NestJS", "Firebase Auth", "Firestore", "CSS Modules", "TypeScript"],
    challenges: [
      "Structuring a clean NestJS backend with Firebase Firestore as database",
      "Real-time task updates across concurrent users with Firestore listeners",
      "Building a responsive UI with CSS Modules at scale",
    ],
    gains: [
      "NestJS architecture and dependency injection patterns",
      "Firebase Auth + Firestore integration in full-stack app",
      "State management for nested project/task data structures",
    ],
    githubUrl: null,
    liveUrl: null,
    imgSrc: null,
    videoSrc: "/projects/projects-management-app.mp4",
    videoSrc2: null,
    pdfSrc: null,
  },
  {
    id: "pathfinder",
    title: "PathFinder — Training Centre Discovery Platform",
    type: "startup",
    description: "Co-founded during the Supernova national startup incubation program. PathFinder is a web platform helping Tunisian students discover and compare training centres, courses, and career paths. Built from idea to working prototype.",
    tech: ["Angular", "PHP", "MySQL", "Bootstrap", "REST API"],
    challenges: [
      "Pivoting product direction based on early user feedback during incubation",
      "Building a functional prototype under startup time pressure",
      "Coordinating development alongside business planning and pitching",
    ],
    gains: [
      "Full startup cycle: ideation, validation, development, pitch",
      "Angular frontend architecture and routing",
      "Experience working within an incubation ecosystem",
    ],
    githubUrl: "https://github.com/ela2002/PathFinder_",
    liveUrl: null,
    imgSrc: "/projects/pathfinder1.png",
    imgSrc2: "/projects/pathfinder2.png",
    videoSrc: null,
    pdfSrc: "/enterpreneurship/pathfinder.pdf",
  },
  {
    id: "voices",
    title: "Voices of Change",
    type: "professional",
    description: "AI-powered multilingual storytelling platform for the Rosa Luxemburg Stiftung. Amplifies marginalised community voices across North Africa through NLP-driven content translation, analysis, and moderation.",
    tech: ["React", "TypeScript", "Node.js", "NLP / AI", "Firebase", "Python"],
    challenges: [
      "Designing a multilingual architecture supporting Arabic, French and English",
      "Building responsible AI content moderation for sensitive community stories",
      "Aligning technical architecture with the NGO's human-rights mission",
    ],
    gains: [
      "AI/NLP pipeline integration in a production React app",
      "Working with international NGO stakeholders on platform strategy",
      "Cross-cultural UX considerations for North African audiences",
    ],
    githubUrl: null,
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: null,
  },
  {
    id: "traffic",
    title: "Traffic Violation Detection",
    type: "academic",
    description: "Computer vision system for detecting traffic violations in video streams using object detection and tracking. Identifies speeding, wrong-way driving, and traffic light violations.",
    tech: ["Python", "OpenCV", "Computer Vision", "Object Detection", "NumPy"],
    challenges: [
      "Accurate vehicle tracking across frames with occlusions",
      "Calibrating speed estimation from pixel displacement",
      "Minimising false positives in real-world noisy video",
    ],
    gains: [
      "Video processing and frame-by-frame object tracking",
      "Real-time computer vision pipeline design",
      "Applied computer vision for safety-critical applications",
    ],
    githubUrl: "https://github.com/ela2002/TrafficViolationDetectionProject",
    liveUrl: null,
    imgSrc: "/projects/violation.png",
    videoSrc: null,
    pdfSrc: "/projects/violation.pdf",
  },
  {
    id: "cim",
    title: "CIM Volume Calculator & Website",
    type: "professional",
    description: "Complete website for Carthage International Moving including a custom interactive volume calculator. Users add furniture items to get a real-time estimate of moving volume and cost. Built with React + Node.js.",
    tech: ["React.js", "Node.js", "Express.js", "MongoDB", "CSS"],
    challenges: [
      "Building an intuitive drag-and-interact furniture volume estimator",
      "Translating complex business pricing logic into clean frontend state",
      "Delivering a client-facing production website independently",
    ],
    gains: [
      "Freelance client communication and requirement gathering",
      "Full-stack development with React + Node.js + MongoDB",
      "Product thinking for a non-technical client",
    ],
    githubUrl: "https://github.com/ela2002/CIM-Moving",
    liveUrl: "https://www.cim-moving.com/",
    imgSrc: null,
    videoSrc: null,
    pdfSrc: null,
  },
  {
    id: "boston",
    title: "Boston Crime Data Analysis",
    type: "academic",
    description: "Exploratory and statistical analysis of Boston Police Department crime incident data. Identifies temporal crime patterns, geographic hotspots, district comparisons, and offense category trends using Python visualisation tools.",
    tech: ["Python", "Pandas", "Matplotlib", "Seaborn", "NumPy", "Jupyter"],
    challenges: [
      "Parsing and cleaning inconsistent timestamps and location entries",
      "Designing meaningful aggregations across time, district, and crime type",
      "Communicating complex spatial patterns through clear 2D visualisations",
    ],
    gains: [
      "Exploratory data analysis (EDA) on a real public dataset",
      "Time series decomposition for crime frequency patterns",
      "Data storytelling for a public safety domain",
    ],
    githubUrl: null,
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: "/projects/Pr%C3%A9sentation%20-%20analyse%20des%20donn%C3%A9es%20criminelles%20de%20Boston.pdf",
  },
  {
    id: "ooredoo",
    title: "Ooredoo Tunisia — Strategic Marketing Analysis",
    type: "startup",
    description: "Comprehensive strategic analysis of Ooredoo Tunisia's marketing positioning and competitive landscape. Includes brand audit, customer segmentation strategy, digital channel evaluation, and strategic recommendations.",
    tech: ["Strategic Analysis", "Market Research", "Brand Strategy", "Competitive Intelligence", "Presentation Design"],
    challenges: [
      "Analysing a major telecom operator's strategy in a highly competitive market",
      "Structuring complex strategic data into a clear, actionable presentation",
      "Developing realistic recommendations within market constraints",
    ],
    gains: [
      "Strategic marketing frameworks applied to a real telecom brand",
      "Market segmentation and competitive positioning analysis",
      "Business communication and professional presentation skills",
    ],
    githubUrl: null,
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: "/enterpreneurship/D%C3%A9marche%20strat%C3%A9gique%20d'Ooredoo%20Tunisie%20.pdf",
  },
  {
    id: "logistics",
    title: "Logistics & Supply Chain — Business Pitch",
    type: "startup",
    description: "Business project presentation covering logistics and supply chain optimisation. Developed as part of an entrepreneurship or business course, addressing operational challenges and proposing an innovative solution.",
    tech: ["Business Modelling", "Supply Chain", "Presentation Design", "Market Analysis"],
    challenges: [
      "Translating logistics complexity into a compelling business narrative",
      "Designing a scalable solution model within real operational constraints",
    ],
    gains: [
      "Business model canvas and value proposition design",
      "Supply chain domain knowledge",
      "Entrepreneurial pitch and storytelling skills",
    ],
    githubUrl: null,
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: "/enterpreneurship/Orange%20Maodern%20Logistic%20Presentation.pdf",
  },
  {
    id: "grey-project",
    title: "Business Strategy Project Presentation",
    type: "startup",
    description: "Formal business project presentation developed during an entrepreneurship or management module. Covers project scope, methodology, market context, and proposed solution in a professional format.",
    tech: ["Business Strategy", "Project Management", "Presentation Design"],
    challenges: [
      "Structuring a professional business pitch within academic constraints",
      "Balancing depth of analysis with clear, concise communication",
    ],
    gains: [
      "Professional business presentation design",
      "Strategic thinking and problem framing",
      "Formal academic communication",
    ],
    githubUrl: null,
    liveUrl: null,
    imgSrc: null,
    videoSrc: null,
    pdfSrc: "/enterpreneurship/Grey%20minimalist%20business%20project%20presentation%20.pdf",
  },
];

// ─── Type badges ──────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  academic:     { label: "Academic",     icon: <FaFlask />,    cls: "proj-badge--academic" },
  professional: { label: "Professional", icon: <FaBriefcase />, cls: "proj-badge--pro" },
  startup:      { label: "Startup",      icon: <FaRocket />,   cls: "proj-badge--startup" },
};

// ─── ProjectMedia ─────────────────────────────────────────────────────────
const ProjectMedia = ({ imgSrc, imgSrc2, videoSrc, videoSrc2, pdfSrc }) => {
  const hasAny = videoSrc || videoSrc2 || imgSrc || imgSrc2 || pdfSrc;
  if (!hasAny) {
    return (
      <div className="proj-media-placeholder">
        <span>🖼️</span>
        <span>Screenshots &amp; demo coming soon</span>
      </div>
    );
  }
  return (
    <div className="proj-media-stack">
      {videoSrc && (
        <MediaEmbed src={videoSrc} type="video" label="Demo video" />
      )}
      {videoSrc2 && (
        <MediaEmbed src={videoSrc2} type="video" label="App walkthrough" />
      )}
      {(imgSrc || imgSrc2) && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {imgSrc && (
            <img
              src={imgSrc}
              alt="Project screenshot"
              className="proj-card__img"
              style={{ flex: imgSrc2 ? "1 1 45%" : "1 1 100%" }}
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          {imgSrc2 && (
            <img
              src={imgSrc2}
              alt="Project screenshot 2"
              className="proj-card__img"
              style={{ flex: "1 1 45%" }}
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
        </div>
      )}
      {pdfSrc && (
        <MediaEmbed
          src={pdfSrc}
          type="pdf"
          label="View presentation"
          contextNote="Presentation slides — may be in French."
          lang="fr"
        />
      )}
    </div>
  );
};

// ─── ProjectCard ──────────────────────────────────────────────────────────
const ProjectCard = ({ project }) => {
  const [open, setOpen] = useState(false);
  const cfg = TYPE_CONFIG[project.type];

  return (
    <div className={`proj-card ${open ? "proj-card--open" : ""}`}>
      {/* Header */}
      <button className="proj-card__header" onClick={() => setOpen(!open)}>
        <div className="proj-card__header-top">
          <span className={`proj-badge ${cfg.cls}`}>{cfg.icon} {cfg.label}</span>
          <span className="proj-card__chevron">
            <FaChevronDown />
          </span>
        </div>
        <div className="proj-card__title">{project.title}</div>
        <div className="proj-card__tech-preview">
          {project.tech.slice(0, 3).map((t, i) => (
            <span key={i} className="proj-tech-chip">{t}</span>
          ))}
          {project.tech.length > 3 && (
            <span className="proj-tech-chip proj-tech-chip--more">+{project.tech.length - 3}</span>
          )}
        </div>
      </button>

      {/* Expanded body */}
      {open && (
        <div className="proj-card__body">
          {/* Media — top of card so it's immediately visible */}
          <ProjectMedia imgSrc={project.imgSrc} imgSrc2={project.imgSrc2} videoSrc={project.videoSrc} videoSrc2={project.videoSrc2} pdfSrc={project.pdfSrc} />

          <p className="proj-description">{project.description}</p>

          {/* Full tech */}
          <div className="proj-section">
            <div className="proj-section__label">🛠 Technologies</div>
            <div className="proj-tech-list">
              {project.tech.map((t, i) => (
                <span key={i} className="proj-tech-chip proj-tech-chip--full">{t}</span>
              ))}
            </div>
          </div>

          {/* Challenges */}
          <div className="proj-section">
            <div className="proj-section__label">⚡ Challenges</div>
            <ul className="proj-list">
              {project.challenges.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>

          {/* Gains */}
          <div className="proj-section">
            <div className="proj-section__label">🎯 What I Learned</div>
            <ul className="proj-list proj-list--gains">
              {project.gains.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          </div>

          {/* Links */}
          <div className="proj-actions">
            {project.githubUrl ? (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="proj-btn proj-btn--github"
              >
                <FaGithub /> View on GitHub
              </a>
            ) : (
              <span className="proj-btn proj-btn--soon">GitHub coming soon</span>
            )}
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="proj-btn proj-btn--live"
              >
                <FaGlobe /> Live Site
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Projects ─────────────────────────────────────────────────────────────
const Projects = () => {
  const [filter, setFilter] = useState("all");
  const types = ["all", "academic", "professional", "startup"];

  const filtered = filter === "all"
    ? PROJECTS
    : PROJECTS.filter((p) => p.type === filter);

  return (
    <div className="projects-section">
      <h2 className="section-title">Projects</h2>
      <p className="proj-intro">
        15 projects spanning ML/AI, full-stack web, mobile, Big Data, and computer vision.
        Click any card to see full details, tech stack, and challenges.
      </p>

      {/* Filter tabs */}
      <div className="proj-filters">
        {types.map((t) => (
          <button
            key={t}
            className={`proj-filter-btn ${filter === t ? "proj-filter-btn--active" : ""}`}
            onClick={() => setFilter(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="projects-list">
        {filtered.map((p) => (
          <ProjectCard key={p.id} project={p} />
        ))}
      </div>
    </div>
  );
};

export default Projects;
